import { AfterContentChecked, Component, ElementRef, OnInit, ViewChild, Renderer2, Inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort, Sort } from '@angular/material/sort';
import { MatPaginator, MatPaginatorDefaultOptions, PageEvent } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import { CertificatesPrivateService, FeedMessageService, UtilsService, showSuccess, UserService, PermissionsService } from '@services/index.service';
import { DialogPrivateCertUploadComponent } from './dialog-private-cert-upload.component';
import { DialogPrivateCertDestroyComponent } from './dialog-private-cert-destroy.component';
import { DialogPrivateCertRemovePin2Component } from './dialog-private-cert-remove-pin2.component';
import { environment } from '@environments/environment';
import { DOCUMENT } from '@angular/common';
import { DialogPrivateCertEditPinComponent } from './dialog-private-cert-edit-pin.component';
import { DialogPrivateCertRegeneratePinComponent } from './dialog-private-cert-regenerate-pin.component';
import { DialogConfirmFpBackupComponent } from './dialog-confirm-fp-backup.component';
import { OrderMode, PersonalCertificateOrderBy, Response, PersonalCertificate, CertificateType, PersonalCertificateCapabilities, 
  PagedTableResponse, PagedTableRequest, PreferenceRequest, PreferenceResponse, PersonalCertificateIds, PersonalCertificateId, 
  UserPreferenceListConfig, Permission, UserPreferenceSourceType, ErrorCode, 
  RemovePinRequest} from '@models/index.model';
import { catchError, concatMap, filter, finalize, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { MatDialogConfig } from '@angular/material/dialog';

@Component({
    selector: 'app-private-certificates',
    templateUrl: './private-certificates.component.html',
    styles: [],
    standalone: false
})
export class PrivateCertificatesComponent implements OnInit, AfterContentChecked {
  @ViewChild('listParent', { static: true }) listParent: ElementRef;
  @ViewChild('listTop', { static: true }) listTop: ElementRef;
  @ViewChild('listContent', { static: true }) listContent: ElementRef;
  listDinamycHeight: any;
  pantResolution: number;
  // App
  public theme = environment.THEME;
  public currentLang: string;

  private readonly _defaultUserPreference: UserPreferenceListConfig = {
    orderBy: PersonalCertificateOrderBy.EXPIRY_TIME,
    orderMode: OrderMode.ASC,
    pageSize: 100,
  };
  private readonly _basePreferenceRequest: PreferenceRequest = {
    source: UserPreferenceSourceType.PersonalArea,
    key: 'personal-certificates',
    value: null,
  };
  public userPreference: UserPreferenceListConfig = null;
  private readonly _personalCertsRequest: PagedTableRequest = {
    filter: '',
    blockNumber: 0,
    orderBy: PersonalCertificateOrderBy.EXPIRY_TIME,
    orderMode: OrderMode.ASC,
    itemsPerBlock: 1000,
  };
  public defaultPaginationOpts: MatPaginatorDefaultOptions = {
    pageSizeOptions: [5, 10, 20, 50, 100],
    showFirstLastButtons: false,
    pageSize: this._defaultUserPreference.pageSize,
  };
  public PersonalCertificateCapabilities = PersonalCertificateCapabilities;
  public CertificateType = CertificateType;
  public certificateUpload = false;

  // Spinners
  public loadingInfo: boolean = true;
  public sendingInfo: boolean = false;
  public checkAllLoading: boolean = false;

  // Table
  public displayedColumnsPersonalCert: string[] = ['active', 'alias', 'type', 'resetPIN', 'expiryTime', 'columnAction'];
  public dataSource: MatTableDataSource<PersonalCertificate> = new MatTableDataSource();
  public rowsCount: number;

  // Table Sorting
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  private readonly _sortDirectionToNumber = {
    asc: OrderMode.ASC,
    desc: OrderMode.DESC,
  };
  public numberToSortDirection = {
    0: 'asc',
    1: 'desc',
  };
  public numberToActiveSortField = {
    1: 'alias',
    2: 'expiryTime',
    3: 'type',
    4: 'resetPIN',
    5: 'active',
  };
  private readonly _activeSortFieldToNumber = {
    alias: PersonalCertificateOrderBy.ALIAS,
    expiryTime: PersonalCertificateOrderBy.EXPIRY_TIME,
    type: PersonalCertificateOrderBy.TYPE,
    resetPIN: PersonalCertificateOrderBy.RESET_PIN,
    active: PersonalCertificateOrderBy.ACTIVE,
  };

  // Table Pagination
  private paginator: MatPaginator;
  @ViewChild(MatPaginator) set paginatorChange(paginator: MatPaginator) {
    if (paginator) {
      this.paginator = paginator;
      this.dataSource.paginator = paginator;
      setTimeout(() => (this.dataSource.paginator.length = this.rowsCount));
    }
  }

  // Table Filtering
  private readonly _filterPredicate = (dataRow: PersonalCertificate, filterValue: string) => dataRow.alias.includes(filterValue);

  constructor(
    private readonly _translate: TranslateService,
    private readonly _dialog: MatDialog,
    private readonly _notify: FeedMessageService,
    private readonly _utils: UtilsService,
    private readonly _personalCertSvc: CertificatesPrivateService,
    private readonly _userServ: UserService,
    private readonly _permiss: PermissionsService,
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  public ngOnInit() {
    this.theme = this._utils.titleCaseWord(this.theme);
    this.currentLang = this._translate.currentLang;
    this.dataSource.filterPredicate = this._filterPredicate;
    this.certificateUpload = this._permiss.hasPermiss(Permission.UPLOAD_CERTIFICATES);
    this._initPersonalCertsWithUserPreferences(this._getUserPreference()).subscribe();
  }

  ngAfterContentChecked() {
    this.pantResolution = window.innerWidth;
    if (this.listContent && (this.pantResolution >= 992)) {
      this.listDinamycHeight = this.listParent.nativeElement.offsetHeight - this.listTop.nativeElement.offsetHeight;
    }
  }

  scrollReset() {
    const table = this.document.querySelector('.matCustomTable');
    if (table) {
      this.renderer.setProperty(table, 'scrollTop', 0);
      this.renderer.setProperty(table, 'scrollLeft', 0);
    }
  }

  private _initPersonalCertsWithUserPreferences(
    userPreference$: Observable<PreferenceResponse>
  ): Observable<PagedTableResponse<PersonalCertificate>> {
    return userPreference$.pipe(
      map((res: PreferenceResponse) => {
        return res?.value ? this._utils.parseUserPreferenceConfig(res.value) : this._defaultUserPreference;
      }),
      catchError((err) => {
        if (err.status === 401 && err.error.errorCode === ErrorCode.UNAUTHORIZED) {
          return throwError(() => err);
        }
        return of(this._defaultUserPreference)
      }),
      concatMap((currentUserPrefrence: UserPreferenceListConfig) => {
        this.userPreference = currentUserPrefrence;
        this.defaultPaginationOpts.pageSize = currentUserPrefrence.pageSize;
        this._personalCertsRequest.orderBy = currentUserPrefrence.orderBy;
        this._personalCertsRequest.orderMode = currentUserPrefrence.orderMode;
        this._personalCertsRequest.blockNumber = 0;

        return this._getPersonalCertificates(this._personalCertsRequest);
      })
    );
  }

  // SORTING
  public sortChange(sortChange: Sort): void {
    const currentOrderBy = this._activeSortFieldToNumber[sortChange.active];
    const currentOrderMode = this._sortDirectionToNumber[sortChange.direction];
    if (this.userPreference.orderBy !== currentOrderBy || this.userPreference.orderMode !== currentOrderMode) {
      this.userPreference.orderBy = currentOrderBy;
      this.userPreference.orderMode = currentOrderMode;
      const userPreferenceRequest = {
        ...this._basePreferenceRequest,
        value: this._utils.stringifyUserPreferenceConfig(this.userPreference),
      };

      this._personalCertsRequest.orderBy = currentOrderBy;
      this._personalCertsRequest.orderMode = currentOrderMode;
      this._personalCertsRequest.blockNumber = 0;
      this._resetPageIndex();

      this._getPersonalCertificates(this._personalCertsRequest)
        .pipe(concatMap(_ => this._setUserPreference(userPreferenceRequest)))
        .subscribe();
    }
  }

  // FILTER
  public applyFilter(filterValue: string): void {
    this._personalCertsRequest.filter = filterValue?.trim().toLowerCase();
    this._personalCertsRequest.blockNumber = 0;
    this._resetPageIndex();

    this._getPersonalCertificates(this._personalCertsRequest).subscribe();
  }

  // PAGINATION
  public paginationChange(paginationChange: PageEvent) {
    const updatePageSize: boolean = this.userPreference.pageSize !== paginationChange.pageSize;
    let userPreferenceRequest: PreferenceRequest = null;
    if (updatePageSize) {
      this._resetPageIndex();
      this.userPreference.pageSize = paginationChange.pageSize;
      userPreferenceRequest = {
        ...this._basePreferenceRequest,
        value: this._utils.stringifyUserPreferenceConfig(this.userPreference),
      };
      this.defaultPaginationOpts.pageSize = this.userPreference.pageSize;
    }

    const currentBlockNumber = this._calculateBlockNumber(
      paginationChange.pageIndex,
      this.userPreference.pageSize,
      this._personalCertsRequest.itemsPerBlock
    );
    const updateBlockNumber: boolean = currentBlockNumber !== this._personalCertsRequest.blockNumber;
    this._personalCertsRequest.blockNumber = currentBlockNumber;

    if (updatePageSize && updateBlockNumber) {
      this._getPersonalCertificates(this._personalCertsRequest)
        .pipe(concatMap(_ => this._setUserPreference(userPreferenceRequest)))
        .subscribe();
    } else {
      if (updatePageSize) {
        this._setUserPreference(userPreferenceRequest).subscribe();
      }
      if (updateBlockNumber) {
        this._getPersonalCertificates(this._personalCertsRequest).subscribe();
      }
    }
    this.scrollReset();
  }

  private _resetPageIndex() {
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
  }

  private _calculateBlockNumber(pageIndex: number, pageSize: number, itemsPerBlock: number) {
    return Math.floor((pageIndex * pageSize) / itemsPerBlock);
  }

  // CAPABILITIES
  public hasCapability(capabilities: PersonalCertificateCapabilities[], key: PersonalCertificateCapabilities): boolean {
    return capabilities.includes(key);
  }

  public onDeleteGlobalCert(selectedCertificates: Array<number>): void {
    const dialogConfig: MatDialogConfig = {
      width: '450px',
      disableClose: true,
      data: {
        alias: null,
        size: selectedCertificates.length,
      },
    };

    const personalCertificateIds: PersonalCertificateIds = {
      personalCertificateIds: selectedCertificates,
    };

    const dialogRef = this._dialog.open(DialogPrivateCertDestroyComponent, dialogConfig);
    this._deleteCertsAfterCloseDialog(dialogRef, personalCertificateIds).subscribe();
  }

  public onDeleteIndividualCert(certificateId: number, certificateAlias: string): void {
    const dialogConfig: MatDialogConfig = {
      width: '450px',
      disableClose: true,
      data: {
        alias: certificateAlias,
        size: 1,
      },
    };

    const personalCertificateIds: PersonalCertificateIds = {
      personalCertificateIds: [certificateId],
    };

    const dialogRef = this._dialog.open(DialogPrivateCertDestroyComponent, dialogConfig);
    this._deleteCertsAfterCloseDialog(dialogRef, personalCertificateIds).subscribe();
  }

  private _deleteCertsAfterCloseDialog(
    dialogRef: MatDialogRef<any>,
    personalCertificateIds: PersonalCertificateIds
  ): Observable<PagedTableResponse<PersonalCertificate>> {
    return dialogRef.afterClosed().pipe(
      filter((res: boolean) => res),
      switchMap(_ => this._deletePersonalCertificate(personalCertificateIds)),
      concatMap(_ => {
        this._resetPageIndex();
        this._personalCertsRequest.blockNumber = 0;
        return this._getPersonalCertificates(this._personalCertsRequest);
      })
    );
  }

  public removeCertPin(id: number, alias: string): void {
    const dialogConfig: MatDialogConfig = {
      width: '400px',
      disableClose: true,
      data: {
        id: id,
        alias: alias
      },
    };
    const dialogRef = this._dialog.open(DialogPrivateCertRemovePin2Component, dialogConfig);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this._resetPageIndex();
        this._personalCertsRequest.blockNumber = 0;
        this._getPersonalCertificates(this._personalCertsRequest).subscribe();
      }
    });
  }

  public uploadCert(): void {
    const dialogConfig: MatDialogConfig = {
      width: '500px',
      disableClose: true,
      data: {
        toPassword: '',
        toPin: '',
        fileUpload: {},
      },
    };
    const dialogRef = this._dialog.open(DialogPrivateCertUploadComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this._resetPageIndex();
        this._personalCertsRequest.blockNumber = 0;
        this._getPersonalCertificates(this._personalCertsRequest).subscribe();
      }
    });
  }

  public changePin(id: number): void {
    const dialogConfig: MatDialogConfig = {
      width: '400px',
      disableClose: true,
      data: {
        id: id,
      },
    };
    const dialogRef = this._dialog.open(DialogPrivateCertEditPinComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this._resetPageIndex();
        this._personalCertsRequest.blockNumber = 0;
        this._getPersonalCertificates(this._personalCertsRequest).subscribe();
      }
    });
  }

  public resetPin(id: number): void {
    const dialogRef = this._dialog.open(DialogPrivateCertRegeneratePinComponent, {
      width: '400px',
      disableClose: true,
      data: {
        id: id,
      },
    });
    dialogRef.disableClose = true;
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this._resetPageIndex();
        this._personalCertsRequest.blockNumber = 0;
        this._getPersonalCertificates(this._personalCertsRequest).subscribe();
      }
    });
  }

  public openDialogConfirmFpBackup(requestId: number): void {
    const dialogConfig: MatDialogConfig = {
      width: '400px',
      disableClose: true,
      data: {
        certificateRequestId: requestId,
        passwordToEncrypt: '',
        passwordToEncryptConfirm: '',
        downloadingCode: '',
      },
    };
    const dialogRef = this._dialog.open(DialogConfirmFpBackupComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(refreshList => {
      if (refreshList) {
        this._resetPageIndex();
        this._personalCertsRequest.blockNumber = 0;
        this._getPersonalCertificates(this._personalCertsRequest).subscribe();
      }
    });
  }

  // QUERIES
  private _getPersonalCertificates(
    personalCertsRequest: PagedTableRequest
  ): Observable<PagedTableResponse<PersonalCertificate>> {
    this.sendingInfo = true;
    return this._personalCertSvc.getPersonalCertificates(personalCertsRequest).pipe(
      map((res: Response<PagedTableResponse<PersonalCertificate>>) => res.data),
      tap((res: PagedTableResponse<PersonalCertificate>) => {
        this.dataSource.data = res.items;
        this.rowsCount = res.totalItems;
        this.scrollReset();
      }),
      finalize(() => {
        this.sendingInfo = false;
        this.loadingInfo = false;
      })
    );
  }

  private _deletePersonalCertificate(
    deletePersonalCertsRequest: PersonalCertificateIds
  ): Observable<Response<Array<number>>> {
    this.sendingInfo = true;

    return this._personalCertSvc.deletePersonalCertificate(deletePersonalCertsRequest).pipe(
      tap((res: Response<Array<number>>) => this._notify.feedBackResult(res.messageType, res.message, showSuccess)),
      finalize(() => {
        this.sendingInfo = false;
      })
    );
  }

  private _getUserPreference(): Observable<PreferenceResponse> {
    return this._userServ.getUserPreference(this._basePreferenceRequest).pipe(
      map((res: Response<PreferenceResponse>) => res.data),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  private _setUserPreference(preferenceRequest: PreferenceRequest): Observable<PreferenceResponse> {
    return this._userServ.setUserPreference(preferenceRequest).pipe(
      map((res: Response<PreferenceResponse>) => res.data),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }
}
